// Pace University: public college page, official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Pace's Common Data Set is not public: the Office of Planning,
//    Assessment and Institutional Research's University Facts and Figures
//    page lists the University Wide Common Data Set 2025-2026 but says it
//    "requires Pace Login" (the link opens a Pace SharePoint sign-in). The
//    first-year counts therefore come from the U.S. Department of
//    Education's IPEDS Admissions survey for Fall 2024, first-time
//    degree-seeking students (College Navigator and the ADM2024 complete
//    data file, unit 194310, both campuses reported together).
//  - Pace Undergraduate Admission: How to Apply (the Fall 2027 deadlines
//    table, required documents, testing, honors college), Key Dates and
//    Deadlines (Fall 2027 Admission), Early Action and Early Decision
//    (binding terms, Regular Decision notification), Program Specific
//    Requirements (performing arts, nursing) and the Test Optional Policy.
//    Quirks: the Early Decision page also says ED applicants hear "by
//    Thanksgiving", while both deadline tables give December 1; the Key
//    Dates page gives January 3 for the performing arts pre-screen, the
//    program page January 4, 2027; How to Apply's table puts February 15 in
//    the Regular Decision notification column, while the Early Decision page
//    says Regular Decision applicants hear on a rolling basis after the
//    deadline. The page uses the tables, the program page and the rolling
//    wording, and says so in the deadline note. The May 1, 2027 deposit date
//    is from Key Dates and Deadlines.
//  - Pace Undergraduate Admission, Cost of Attendance, 2026-2027 full-time
//    undergraduate. Pace lists the items with separate fees and food and
//    housing for the New York City and Westchester campuses and gives no
//    total; the totals here are the sums of the items. The $1,000 books line
//    is left out because Pace says it "will only apply if you decide to opt
//    out of the book fee" (books are now inside the standard fees).
//  - College Scorecard, school id 194310 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    ipeds: {
        label: 'IPEDS Admissions, Fall 2024, U.S. Department of Education',
        url: 'https://nces.ed.gov/collegenavigator/?id=194310#admsns',
        read: '2026-10-01',
    },
    opair: {
        label: 'Pace Planning, Assessment and Institutional Research, University Facts and Figures',
        url: 'https://www.pace.edu/opair/university-facts-and-figures',
        read: '2026-10-01',
    },
    howToApply: {
        label: 'Pace Undergraduate Admission, How to Apply',
        url: 'https://www.pace.edu/admission-and-aid/undergraduate-admission/how-apply',
        read: '2026-10-01',
    },
    keyDates: {
        label: 'Pace Undergraduate Admission, Key Dates and Deadlines',
        url: 'https://www.pace.edu/admission-and-aid/undergraduate-admission/how-apply/key-dates-and-deadlines',
        read: '2026-10-01',
    },
    earlyPlans: {
        label: 'Pace Undergraduate Admission, Early Action and Early Decision',
        url: 'https://www.pace.edu/admission-and-aid/undergraduate-admission/how-apply/early-action-and-early-decision',
        read: '2026-10-01',
    },
    programReqs: {
        label: 'Pace Undergraduate Admission, Program Specific Requirements',
        url: 'https://www.pace.edu/admission-and-aid/undergraduate-admission/how-apply/program-specific-requirements',
        read: '2026-10-01',
    },
    tests: {
        label: 'Pace Undergraduate Admission, Test Optional Policy',
        url: 'https://www.pace.edu/admission-and-aid/undergraduate-admission/how-apply/test-optional-policy',
        read: '2026-10-01',
    },
    costs: {
        label: 'Pace Undergraduate Admission, Cost of Attendance',
        url: 'https://www.pace.edu/admission-and-aid/undergraduate-admission/tuition-and-cost/cost-of-attendance',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?194310-Pace-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'pace-university',
    kbId: 'pace_university',
    name: 'Pace University',
    shortName: 'Pace',
    aliases: [],
    location: 'New York, NY',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Pace design guidelines); decoration only.
    branding: {
        primary: '#002D72',
        secondary: '#FFC72C',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/pace_university_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: null,
        applied: 30030,
        admitted: 22798,
        enrolled: 1850,
        source: 'ipeds',
        where: 'first-time, degree-seeking undergraduates, New York City and Westchester campuses together',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Pace publishes no first-year acceptance rate by college or major. Its Common Data Set is posted only behind a Pace login, so the counts above come from the federal IPEDS survey.',
        source: 'opair',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Pace and name a major. Some programs ask for more:',
        rows: [
            { school: 'Sands College of Performing Arts (New York City campus only)', requirement: 'Apply by December 1, 2026 and upload your pre-screen by January 4, 2027. An audition or interview is required, and you hear about the audition and academic admission on or before March 31, 2027. Performing arts applicants cannot apply Early Action.' },
            { school: 'Nursing, four-year BS (Westchester campus only)', requirement: 'First-year applications are due February 15. Pace says admission to the program is competitive, and clinical courses begin in the fall only.' },
            { school: 'Pforzheimer Honors College', requirement: 'Every applicant is considered, based on GPA and test scores if sent. You need at least a 90 GPA, and you can apply test-optional.', source: 'howToApply' },
        ],
        source: 'programReqs',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'February 15' },
        rows: [
            { plan: 'Early Decision', deadline: 'November 1', decision: 'December 1', binding: true },
            { plan: 'Early Action', deadline: 'November 15', decision: 'December 15', binding: false },
            { plan: 'Performing Arts', deadline: 'December 1', decision: 'March 31', binding: false },
            { plan: 'Regular Decision and Nursing', deadline: 'February 15', decision: 'Rolling, after February 15', binding: false },
        ],
        note: 'The application opens August 1, 2026. FAFSA priority deadlines are November 15 for Early Decision, December 1 for Early Action and February 15 for Regular Decision. The application fee is $50, waived for the Westchester campus. Accepted students other than Early Decision deposit by May 1, 2027. Pace\'s Early Action and Early Decision page says Regular Decision applicants hear on a rolling basis after the deadline, and that Early Decision applicants hear by Thanksgiving.',
        source: 'howToApply',
        binding: 'Early Decision is binding: if you are admitted and are comfortable with your financial aid offer, you agree to enroll at Pace and must withdraw your other applications. You can apply Early Decision to only one school, and you submit an Early Decision Agreement signed by you, a parent or guardian and your counselor by November 1. If you are not admitted in Early Decision or Early Action, your application moves to Regular Decision.',
        bindingSource: 'earlyPlans',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Pace does not require the SAT or ACT for most applicants. Without scores you are still considered for a merit scholarship, as every admitted student is except international first-year students. You can switch to applying without scores by emailing the admission office before a decision, but not after one.',
        source: 'tests',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'keyDates',
    },

    cost: {
        year: '2026-2027',
        columns: ['New York City campus', 'Westchester campus'],
        rows: [
            { item: 'Tuition', amounts: [55040, 55040] },
            { item: 'Standard fees (books included)', amounts: [2738, 2838] },
            { item: 'Food and housing (on campus)', amounts: [25400, 20900] },
            { item: 'Personal expenses', amounts: [1850, 1850] },
            { item: 'Transportation', amounts: [1120, 1120] },
        ],
        totals: [86148, 81748],
        note: 'For a full-time undergraduate. Sands College of Performing Arts tuition is $62,040. Food and housing vary with your residence hall, room and meal plan. Pace gives no total; these are the sums of its items.',
        source: 'costs',
        netPrice: {
            average: 30721,
            byIncome: [
                { income: '$0 to $30,000', amount: 27012 },
                { income: '$30,001 to $48,000', amount: 26470 },
                { income: '$48,001 to $75,000', amount: 27645 },
                { income: '$75,001 to $110,000', amount: 30846 },
                { income: 'Over $110,000', amount: 37189 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Pace\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
